import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Logo from './Logo';
import { locationService } from '../../../../services/location.api.service';
import SearchBar from './Search/SearchBar';
import Icons from './Icons';
import './styles/Header.css';
import './styles/headermobile.css';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isVisible] = useState(true);
  // Mobile breakpoint set to 540px - Surface Duo (540px) will use mobile layout
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 540);
  const [userLocation, setUserLocation] = useState<string>('');
  const [deliveryTime, setDeliveryTime] = useState<string>('');
  const [isServiceable, setIsServiceable] = useState<boolean>(true);
  const [isLocationFetched, setIsLocationFetched] = useState(false);
  const [, setIsScrolled] = useState(false);
  
  const isProductDetailsPage = location.pathname.startsWith('/product/');
  const isAuthPage = location.pathname === '/auth';
  const isHome = location.pathname === '/';
  const isViewProductsPage = location.pathname.startsWith('/view-products/');
  const isViewAllPage = location.pathname === '/categories/view-all';
  const shouldShowBackArrow = isViewProductsPage || isProductDetailsPage || isViewAllPage;

  useEffect(() => {
    const handleResize = () => {
      // Mobile breakpoint at 540px to include Surface Duo
      setIsMobile(window.innerWidth <= 540);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchAndSetLocation = async () => {
      // Always try to get geolocation and reverse geocode on every refresh
      if (navigator.geolocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            });
          });
          const { latitude, longitude } = position.coords;
          const res = await locationService.reverseGeocode(latitude, longitude);
          if (res.success && res.data && res.data.pincode) {
            localStorage.setItem('userPincode', res.data.pincode);
            localStorage.setItem('userLocation', res.data.locationName || '');
            localStorage.setItem('userDeliveryTime', res.data.deliveryTime || 'Not Available');
            localStorage.setItem('userIsServiceable', res.data.isServiceable ? 'true' : 'false');
            setUserLocation(res.data.locationName || '');
            setDeliveryTime(res.data.deliveryTime || 'Not Available');
            setIsServiceable(res.data.isServiceable || false);
            setIsLocationFetched(true);
          } else {
            setUserLocation('');
            setDeliveryTime('Not Available');
            setIsServiceable(false);
            setIsLocationFetched(true);
          }
        } catch (err) {
          // User denied location or error: fallback to Gurugram
          localStorage.setItem('userPincode', '122001');
          localStorage.setItem('userLocation', 'Gurugram');
          localStorage.setItem('userDeliveryTime', '30 minutes');
          localStorage.setItem('userIsServiceable', 'false');
          setUserLocation('Gurugram');
          setDeliveryTime('30 minutes');
          setIsServiceable(false);
          setIsLocationFetched(true);
        }
      } else {
        setUserLocation('');
        setDeliveryTime('Not Available');
        setIsServiceable(false);
        setIsLocationFetched(true);
      }
    };
    fetchAndSetLocation();
  }, []);

  return (
    <>
      <header className={`header ${!isVisible ? 'header--hidden' : ''}`}>
        <div className="header-container">
          
          {!isMobile ? (
            <>
              {shouldShowBackArrow && (
                <button
                  onClick={() => {
                    if (isViewAllPage) navigate('/');
                    else if (isViewProductsPage) navigate('/categories/view-all');
                    else navigate(-1);
                  }}
                  className="back-arrow-button"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    marginRight: '8px'
                  }}
                >
                  <ArrowLeft size={20} color="#12111F" />
                </button>
              )}
              <Logo />
              {isLocationFetched && (
                <div className="desktop-delivery-section" style={{
                  marginLeft: isHome ? '3%' : '3%'
                }}>
                  <span className="delivery-text-header">
                    {isServiceable ? (
                      <>Delivery in <span className="delivery-highlight">{deliveryTime === '30 minutes' ? '30 mins' : deliveryTime}</span></>
                    ) : userLocation === 'Gurugram' ? (
                      <>Delivery in <span className="delivery-highlight">30 mins</span></>
                    ) : (
                      <span className="delivery-highlight">Delivery Not Available</span>
                    )}
                  </span>
                  <span className="location-text">
                    {userLocation}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexDirection: 'row'
            }}>
              {shouldShowBackArrow && (
                <button
                  onClick={() => {
                    if (isViewAllPage) navigate('/');
                    else if (isViewProductsPage) navigate('/categories/view-all');
                    else navigate(-1);
                  }}
                  className="back-arrow-button-mobile"
                >
                  <ArrowLeft size={20} color="#12111F" />
                </button>
              )}
              <div className="mobile-logo-section">
                <Logo />
              </div>
              
              {isLocationFetched && !isAuthPage && (
                <div className="mobile-delivery-section">
                  <div className="mobile-delivery-info">
                    <span className="mobile-delivery-text">
                      {isServiceable ? (
                        <>Delivery in <span className="delivery-highlight">{deliveryTime === '30 minutes' ? '30 mins' : deliveryTime}</span></>
                      ) : userLocation === 'Gurugram' ? (
                        <>Delivery in <span className="delivery-highlight">30 mins</span></>
                      ) : (
                        <span className="delivery-highlight">Delivery Not Available</span>
                      )}
                    </span>
                    <span className="mobile-location-text">
                      {userLocation}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {!isMobile && (
            <div className="header-search-pincode-row">
              <SearchBar />
            </div>
          )}
          
          <Icons />
        </div>
      </header>

      {isMobile && !isProductDetailsPage && !isAuthPage && !isViewProductsPage && (
        <div className="mobile-search-container">
          <SearchBar />
        </div>
      )}
    </>
  );
};

export default Header;